import {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {BIService} from '../services/BIService';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {CartService} from '../services/CartService';
import {ICartControllerApi} from '../../types/app.types';
import {OrderService} from '../services/OrderService';
import {ModalManagerService} from '../services/ModalManagerService';
import {getCatalogAppIds, getItemTypes} from '../utils/bi.utils';
import {SPECS} from '../specs';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';

type OrderStoreConfig = {
  cartService: CartService;
  orderService: OrderService;
  biService: BIService;
  styleSettingsService: StyleSettingsService;
  modalManagerService: ModalManagerService;
};

export class OrderStore {
  private readonly biService: BIService;
  private readonly cartService: CartService;
  private readonly controllerApi: ICartControllerApi;
  private readonly modalManagerService: ModalManagerService;
  private readonly orderService: OrderService;
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  private readonly styleSettingsService: StyleSettingsService;
  private readonly taxName: string;

  constructor(
    controllerApi: ICartControllerApi,
    siteStore: SiteStore,
    viewerContext: ViewerContext,
    {biService, styleSettingsService, cartService, orderService, modalManagerService}: OrderStoreConfig
  ) {
    this.controllerApi = controllerApi;
    this.siteStore = siteStore;
    this.viewerContext = viewerContext;
    this.styleSettingsService = styleSettingsService;
    this.biService = biService;
    this.cartService = cartService;
    this.orderService = orderService;
    this.taxName = this.siteStore.taxName;
    this.modalManagerService = modalManagerService;
  }

  private get shouldDisplaySubtotal() {
    const hasGlobalDiscount =
      this.cartService.cartModel.coupon &&
      (this.cartService.cartSummary?.couponDiscount ||
        (this.viewerContext.isExperimentEnabled(SPECS.RevampDiscountsInCartAndCheckout) &&
          this.cartService.cartSummary?.globalDiscounts.length > 0));

    return (
      this.cartService.cartSummary &&
      (this.shouldDisplayDelivery ||
        this.orderService.shouldDisplayTax ||
        this.shouldDisplayAdditionalFees ||
        this.shouldDisplayPlatformFees ||
        hasGlobalDiscount)
    );
  }

  public get shouldDisplayDelivery() {
    const {shouldShowShipping} = this.styleSettingsService;

    return shouldShowShipping && !this.cartService.isNonShippableCart;
  }

  private readonly handleChangeRegion = async () => {
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    if (
      (moveCartToViewerContext && this.viewerContext.getViewMode() === 'Editor') ||
      (!moveCartToViewerContext && this.siteStore.isEditorMode())
    ) {
      return;
    }

    const cartModel = this.cartService.cartModel;
    const address = this.cartService.isNonShippableCart
      ? cartModel.paymentInfo?.address
      : cartModel.deliveryInfo?.address;

    const modalData: any = {
      country: address.country,
      subdivision: address.subdivision,
      zipCode: address.postalCode,
      returnCountryShortKey: true,
      biData: {
        cartId: cartModel.id,
        itemType: getItemTypes(cartModel),
        catalogAppId: getCatalogAppIds(cartModel),
        checkoutId: cartModel.checkoutId,
        purchaseFlowId: cartModel.purchaseFlowId,
      },
    };

    this.biService.clickOnShippingDestinationInCartPageSf(this.cartService.cartModel);

    /* istanbul ignore next */
    const data = ((await this.modalManagerService.modalManger.openChangeRegion(modalData)) as any)?.message;

    if (data?.country) {
      this.biService.updateClickedInSelectYourShippingDestinationPopupSf(this.cartService.cartModel, data);

      this.orderService.destinationWasChanged = true;
      this.cartService.isSummaryUpdating = true;
      await this.controllerApi.updateComponent();
      await this.cartService.setAddress({
        country: data.country,
        subdivision: data.subdivision,
        postalCode: data.zipCode,
      });

      if (!this.cartService.deliverySectionModel?.canShipToDestination) {
        this.biService.errorInCheckoutSf(cartModel);
      }
    } else {
      this.biService.cancelClickedInSelectYourShippingDestinationPopupSf(cartModel);
    }
  };

  private get shouldDisplayDeliveryError() {
    return (
      this.shouldDisplayDelivery &&
      !this.orderService.canShip &&
      this.orderService.destinationWasChanged &&
      !this.cartService.isEmpty &&
      !this.orderService.hasDeliveryOptions &&
      !!this.cartService.cartModel.deliveryInfo?.address.country
    );
  }

  private get shouldDisplayAdditionalFees() {
    if (!this.cartService.cartSummary) {
      return false;
    }
    return this.cartService.cartSummary.additionalFees.length > 0;
  }

  private get shouldDisplaySummaryDiscounts() {
    if (
      !this.cartService.cartSummary ||
      !this.viewerContext.isExperimentEnabled(SPECS.RevampDiscountsInCartAndCheckout)
    ) {
      return false;
    }

    if (this.viewerContext.isExperimentEnabled(SPECS.CouponAlignmentInCartAndCheckout)) {
      return this.cartService.cartSummary.globalDiscounts.length > 0;
    }

    return Boolean(this.cartService.cartSummary.couponDiscount && this.cartService.cartModel.coupon);
  }

  private get shouldDisplayPlatformFees() {
    if (!this.cartService.cartSummary) {
      return false;
    }
    return this.cartService.cartSummary.platformFees.length > 0;
  }

  private get shouldDisplayPayNowPayLater() {
    if (!this.cartService.cartSummary) {
      return false;
    }
    return (
      this.cartService.cartSummary.paymentSummary.payLater.convertedAmount > 0 ||
      this.cartService.cartSummary.paymentSummary.payAfterFreeTrial.convertedAmount > 0
    );
  }

  private get shouldDisplayDeliveryMethodSelection() {
    const hasPickupOption = this.orderService.hasPickupOption;
    const moreThanOneOption = this.cartService.deliverySectionModel?.options?.length > 1;

    return (
      this.shouldDisplayDelivery &&
      !this.cartService.deliverySectionModel?.hasTimeSlots &&
      (moreThanOneOption || hasPickupOption)
    );
  }

  private get shouldHideCountryPicker() {
    const shouldShowCountryPicker =
      (this.cartService.shouldCalculateDelivery() && !this.cartService.deliverySectionModel?.hasTimeSlots) ||
      this.styleSettingsService.shouldShowTax;

    return !shouldShowCountryPicker;
  }

  private readonly selectShippingOption = async (selectedShippingOptionId: string): Promise<void> => {
    this.biService.shippingOptionsSelectedInCart(
      this.cartService.cartModel,
      this.cartService.deliverySectionModel,
      selectedShippingOptionId
    );

    const deliveryMethod = {
      appId: null,
      code: selectedShippingOptionId,
    };
    const fn = () => this.cartService.setShippingOption({deliveryMethod});
    this.cartService.isSummaryUpdating = true;
    await this.controllerApi.updateComponent();
    return fn();
  };

  private readonly reportDeliveryOptionsPresented = () => {
    this.biService.shippingOptionsPresentedInCart(this.cartService.cartModel, this.cartService.deliverySectionModel);
  };

  private get shouldShowConversionDisclaimer(): boolean {
    const {paymentInfo, customerInfo} = this.cartService.cartModel;

    return paymentInfo.currencyCode !== customerInfo.currencyCode;
  }

  public toProps() {
    if (this.cartService.isEmpty) {
      return;
    }

    return {
      isPickup: this.orderService.isPickup(),
      handleChangeRegion: this.handleChangeRegion,
      hasShippingDestination: this.orderService.hasShippingDestination,
      selectShippingOption: this.selectShippingOption,
      shouldDisplayDelivery: this.shouldDisplayDelivery,
      shouldDisplayDeliveryError: this.shouldDisplayDeliveryError,
      shouldDisplayDeliveryMethodSelection: this.shouldDisplayDeliveryMethodSelection,
      shouldDisplaySubtotal: this.shouldDisplaySubtotal,
      shouldDisplayAdditionalFees: this.shouldDisplayAdditionalFees,
      shouldDisplayPlatformFees: this.shouldDisplayPlatformFees,
      shouldDisplaySummaryDiscounts: this.shouldDisplaySummaryDiscounts,
      shouldDisplayPayNowPayLater: this.shouldDisplayPayNowPayLater,
      shouldDisplayTax: this.orderService.shouldDisplayTax,
      shouldHideCountryPicker: this.shouldHideCountryPicker,
      taxName: this.taxName,
      isPricesIncludeTax: this.cartService.cartModel.taxInfo.pricesIncludeTax,
      shouldShowConversionDisclaimer: this.shouldShowConversionDisclaimer,
      isFullAddressRequired: this.cartService.isFullAddressRequired,
      deliverySectionModel: this.cartService.deliverySectionModel,
      reportDeliveryOptionsPresented: this.reportDeliveryOptionsPresented,
    };
  }
}
